import React, { useEffect, useState } from "react";
import {
  fetchParties,
  fetchPreviousDayLedger,
  fetchTodayOrderAmount,
  fetchTodayLedger,
  fetchTodayHawkerCDM,
  saveDayClose,
  getISTDateString,
  fetchOnlineCollectionMap
} from "../services/cashierService";

import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const fmt = (v) => Number(v || 0).toFixed(2);

const UI_THEME = {
  primary: "#4f46e5",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  background: "#f8fafc",
  card: "#ffffff",
  text: "#1e293b",
  muted: "#64748b",
  border: "#e2e8f0",
};

const SectionCard = ({ title, children, icon }) => (
  <div style={{ 
    background: UI_THEME.card, 
    borderRadius: "16px", 
    padding: "20px", 
    marginBottom: "20px", 
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)",
    border: `1px solid ${UI_THEME.border}`
  }}>
    <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", borderBottom: `1px solid ${UI_THEME.border}`, paddingBottom: "10px" }}>
      {icon && <span style={{ marginRight: "10px", fontSize: "20px" }}>{icon}</span>}
      <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "800", color: UI_THEME.text, textTransform: "uppercase", letterSpacing: "0.05em" }}>{title}</h3>
    </div>
    {children}
  </div>
);

const DataRow = ({ label, value, color = UI_THEME.text, size = "18px" }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
    <span style={{ fontSize: "14px", fontWeight: "600", color: UI_THEME.muted }}>{label}</span>
    <span style={{ fontSize: size, fontWeight: "800", color, fontVariantNumeric: "tabular-nums" }}>₹ {value}</span>
  </div>
);

const DenomRow = ({ value, count, onChange }) => {
  const tint = {
    500: "#e0e7ff", // Brighter Indigo
    200: "#ffedd5", // Brighter Orange
    100: "#f3e8ff", // Brighter Purple
    50: "#e0f2fe",  // Brighter Blue
    20: "#fef3c7",  // Brighter Amber
    10: "#fee2e2",  // Brighter Red
    5: "#f1f5f9",   // Brighter Slate
    1: "#ffffff"    // White
  }[value] || "#ffffff";

  const borderColor = {
    500: "#818cf8", 200: "#fb923c", 100: "#c084fc", 50: "#38bdf8",
    20: "#fbbf24", 10: "#f87171", 5: "#cbd5e1", 1: "#94a3b8"
  }[value] || "#e2e8f0";

  const adjust = (delta) => {
    const current = Number(count || 0);
    const newVal = current + delta;
    onChange({ target: { value: newVal } });
  };

  return (
    <div style={{ 
      display: "flex", 
      flexDirection: "column", 
      gap: "8px", 
      padding: "12px", 
      borderRadius: "16px", 
      background: tint, 
      marginBottom: "16px",
      border: `2px solid ${borderColor}`,
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
    }}>
      {/* Top Row: Denomination and Value */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: "22px", fontWeight: "900", color: "#1e293b" }}>₹{value}</div>
        <div style={{ textAlign: "right" }}>
          <span style={{ fontSize: "10px", fontWeight: "800", color: UI_THEME.muted, textTransform: "uppercase", display: "block" }}>VALUE</span>
          <span style={{ fontSize: "18px", fontWeight: "900", color: UI_THEME.primary }}>
            {Number(count || 0) * value === 0 ? "—" : (Number(count || 0) * value).toLocaleString("en-IN")}
          </span>
        </div>
      </div>

      {/* Bottom Row: Quantity Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <button 
          onClick={() => adjust(-1)}
          style={{ 
            width: "55px", height: "55px", borderRadius: "12px", border: "none", 
            background: UI_THEME.danger, color: "#fff", fontSize: "28px", fontWeight: "bold", 
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            flexShrink: 0
          }}
        >
          −
        </button>

        <input
          type="number"
          placeholder="0"
          value={count}
          onChange={onChange}
          style={{ 
            flex: 1,
            height: "55px",
            fontSize: "24px", 
            fontWeight: "900",
            borderRadius: "12px",
            border: "2px solid #cbd5e1",
            textAlign: "center",
            width: "100%",
            boxSizing: "border-box",
            background: "#fff",
            color: "#000",
            padding: 0
          }}
        />

        <button 
          onClick={() => adjust(1)}
          style={{ 
            width: "55px", height: "55px", borderRadius: "12px", border: "none", 
            background: UI_THEME.primary, color: "#fff", fontSize: "28px", fontWeight: "bold", 
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            flexShrink: 0
          }}
        >
          +
        </button>
      </div>
      <style>{`
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      `}</style>
    </div>
  );
};

async function getServerISTTime() {
  try {
    // Using a more reliable API (TimeAPI.io)
    const res = await fetch("https://timeapi.io/api/Time/current/zone?timeZone=Asia/Kolkata", {
      cache: 'no-store'
    });
    if (res.ok) {
      const data = await res.json();
      return new Date(data.dateTime);
    }
  } catch (err) {
    // Silence the error to keep the console clean since we have a fallback
  }
  return new Date(); // Safe fallback to device time
}

function parseTimeStr(timeStr) {
  if (!timeStr) return NaN;
  const str = timeStr.trim().toUpperCase();
  let [time, modifier] = str.split(" ");
  if (!time) return NaN;
  let [hours, minutes] = time.split(":").map(Number);
  
  if (modifier === "PM" && hours < 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;
  
  return (hours * 60) + minutes;
}

export default function Cashier() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [timeBlocked, setTimeBlocked] = React.useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const loadConfig = async () => {
      const ref = doc(db, "settings", "cashierConfig");
      const snap = await getDoc(ref);
      if (snap.exists()) setConfig(snap.data());
      else setConfig({ startTime: "09:45", endTime: "13:30" });

      // Fetch Dynamic Denominations
      const cRef = doc(db, "settings", "currencyConfig");
      const cSnap = await getDoc(cRef);
      if (cSnap.exists() && cSnap.data().denominations) {
        setDenomOrder(cSnap.data().denominations.sort((a, b) => b - a));
      }
    };
    loadConfig();
  }, []);

  const [userProfile, setUserProfile] = useState(null);
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [onlineCollectionMap, setOnlineCollectionMap] = useState({});
  const [onlineCollected, setOnlineCollected] = useState(0);
  const [openingBalance, setOpeningBalance] = useState(null);
  const [orderAmount, setOrderAmount] = useState(0);
  const [cdmAmount, setCdmAmount] = useState(0);
  const [nicheBrandAmount, setNicheBrandAmount] = useState(0);
  const [DENOM_ORDER, setDenomOrder] = useState([500, 200, 100, 50, 20, 10, 5, 1]); // Fallback
  const [denoms, setDenoms] = useState({});

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) { navigate("/"); return; }
      
      const checkUserAndLoadParties = async () => {
        try {
          const userSnap = await getDoc(doc(db, "users", user.uid));
          if (userSnap.exists()) {
            const profile = userSnap.data();
            setUserProfile(profile);
            const userRole = profile.role?.toLowerCase()?.trim();
            const isManager = ["cashier", "admin", "superadmin"].includes(userRole);
            const list = await fetchParties(isManager ? "all" : (profile.partytype || "hawker"), profile.clientId);
            setParties(list);
          } else {
            const list = await fetchParties("hawker");
            setParties(list);
          }
        } catch (err) {
          console.error("Error loading user profile and parties:", err);
        }
      };
      checkUserAndLoadParties();

      const checkServerTime = async () => {
        if (!config) return;
        try {
          const istTime = await getServerISTTime();
          const currentMinutes = (istTime.getHours() * 60) + istTime.getMinutes();
          
          const start = parseTimeStr(config.startTime);
          const end = parseTimeStr(config.endTime);
          
          if (!isNaN(start) && !isNaN(end)) {
            setTimeBlocked(currentMinutes < start || currentMinutes > end);
          } else {
             // Fallback to open if explicitly corrupted string
             setTimeBlocked(false);
          }
        } catch (err) { console.error(err); }
      };
      checkServerTime();
    });
    return () => unsub();
  }, [navigate, config]);

  useEffect(() => {
    const initialDenoms = {};
    DENOM_ORDER.forEach(d => initialDenoms[d] = "");
    setDenoms(initialDenoms);
  }, [DENOM_ORDER]);

  useEffect(() => {
    if (parties.length > 0) {
      const queryParams = new URLSearchParams(window.location.search);
      const queryPartyId = queryParams.get("partyId");
      if (queryPartyId) {
        const exists = parties.some(p => p.partyId === queryPartyId);
        if (exists) {
          setPartyId(queryPartyId);
        }
      }
    }
  }, [parties]);

  const [partyMode, setPartyMode] = useState("Cash");
  const [cashTotal, setCashTotal] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [preview, setPreview] = useState(null);
  const [onlineLoading, setOnlineLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!partyId) return;
    const today = getISTDateString();
    let isMounted = true;
    const loadData = async () => {
      if (parties.length === 0) return;
      const pInfo = parties.find(p => p.partyId === partyId);
      if (!pInfo) return;
      const mode = pInfo.payment_mode || "Cash";
      setPartyMode(mode);
      const todayLedger = await fetchTodayLedger(partyId, today);
      if (!isMounted) return;
      if (todayLedger) {
        setOpeningBalance(todayLedger.openingBalance || 0);
        if (["PENDING", "REJECTED", ""].includes(todayLedger.status || "")) {
          const { total: liveOrders } = await fetchTodayOrderAmount(partyId, today, mode);
          setOrderAmount(liveOrders);
          const { cdmAmount: freshCdm, nicheBrandAmount: freshNiche } = await fetchTodayHawkerCDM(partyId, today);
          setCdmAmount(freshCdm);
          setNicheBrandAmount(freshNiche);
        } else {
          setOrderAmount(todayLedger.orderAmount || 0);
          setCdmAmount(todayLedger.cdmAmount || 0);
          setNicheBrandAmount(todayLedger.nicheBrandAmount || 0);
        }
        if (todayLedger.status === "APPROVED") setOnlineCollected(todayLedger.onlineCollected || 0);
        setDenoms(prev => ({ ...prev, ...(todayLedger.denominations || {}) }));
        if (todayLedger.status === "APPROVED") {
          setPreview({ ...todayLedger, cashTotal: todayLedger.cashCollected });
          setSubmitted(true);
        } else {
          setSubmitted(false);
          setPreview(null);
        }
      } else {
        const prev = await fetchPreviousDayLedger(partyId, today);
        setOpeningBalance(prev?.closingBalance ?? 0);
        const { total: freshOrders } = await fetchTodayOrderAmount(partyId, today, mode);
        setOrderAmount(freshOrders);
        const { cdmAmount: freshCdm, nicheBrandAmount: freshNiche } = await fetchTodayHawkerCDM(partyId, today);
        setCdmAmount(freshCdm);
        setNicheBrandAmount(freshNiche);
        setSubmitted(false);
        setPreview(null);
      }
    };
    setOpeningBalance(null); setOrderAmount(0); setCdmAmount(0); setNicheBrandAmount(0); setOnlineCollected(0); setSubmitted(false); setPreview(null);
    setCountdown(10);
    const timer = setInterval(() => { setCountdown(prev => (prev > 0 ? prev - 1 : 0)); }, 1000);
    loadData();
    return () => { isMounted = false; clearInterval(timer); };
  }, [partyId, parties]);

  useEffect(() => {
    if (openingBalance === null) return;
    let cash = 0;
    DENOM_ORDER.forEach(d => { cash += (denoms[d] || 0) * d; });
    setCashTotal(cash);
    setClosingBalance(Number(openingBalance) + Number(orderAmount) + Number(cdmAmount) + Number(nicheBrandAmount) - Number(onlineCollected) - Number(cash));
  }, [denoms, openingBalance, orderAmount, onlineCollected, DENOM_ORDER]);

  useEffect(() => {
    async function loadOnlineData() {
      const today = getISTDateString();
      setOnlineLoading(true);
      try {
        const data = await fetchOnlineCollectionMap(today);
        setOnlineCollectionMap(data || {});
      } catch (err) { console.error(err); setOnlineCollectionMap({}); }
      finally { setOnlineLoading(false); }
    }
    loadOnlineData();
  }, []);

  useEffect(() => {
    if (!partyId || (submitted && preview?.status === "APPROVED")) return;
    setOnlineCollected(Number(onlineCollectionMap[partyId] || 0));
  }, [partyId, onlineCollectionMap, submitted, preview]);

  const submit = async () => {
    if (submitted || saving || onlineLoading) return;
    setSaving(true);
    try {
      await saveDayClose({ 
        partyId, openingBalance, paymentMode: partyMode, 
        denominations: denoms, onlineCollected, cashCollected: cashTotal 
      });
      setPreview({ partyId, openingBalance, orderAmount, cdmAmount, nicheBrandAmount, cashTotal, onlineCollected, closingBalance });
      setSubmitted(true);
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  };

  if (timeBlocked) {
    return (
      <div style={{ padding: 40, textAlign: "center", background: UI_THEME.background, minHeight: "100vh" }}>
        <button onClick={() => navigate("/base")} style={{ marginBottom: 20, padding: "12px 20px", background: "#fff", border: `1px solid ${UI_THEME.border}`, borderRadius: 12 }}>← Home Page</button>
        <div style={{ background: "#fff", padding: 30, borderRadius: 20, boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)" }}>
          <h2 style={{ color: UI_THEME.danger, fontSize: 32, marginBottom: 10 }}>⛔ Cashier Closed</h2>
          <p style={{ color: UI_THEME.muted, fontSize: 18 }}>Cash collection allowed only between</p>
          <b style={{ fontSize: 24, color: UI_THEME.text }}>{config.startTime} — {config.endTime} IST</b>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: UI_THEME.background, minHeight: "100vh", paddingBottom: "100px", fontFamily: "'Inter', sans-serif" }}>
      <div style={{ maxWidth: 500, margin: "auto", padding: "20px" }}>
        
        <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
          <button onClick={() => navigate("/base")} style={{ background: "#fff", border: `1px solid ${UI_THEME.border}`, padding: "10px 15px", borderRadius: "12px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>←</button>
          <h2 style={{ margin: "0 auto", fontSize: "28px", fontWeight: "900", color: UI_THEME.text }}>Cashier</h2>
        </div>

        {!isOnline && (
          <div style={{
            background: "#fee2e2",
            color: "#b91c1c",
            padding: "12px 16px",
            borderRadius: "14px",
            marginBottom: "16px",
            fontSize: "14px",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            border: "1px solid #fca5a5"
          }}>
            <span>⚠️</span>
            <span>Offline Mode: Collections will sync automatically once internet is recovered.</span>
          </div>
        )}

        {!submitted && (
          <div style={{ marginBottom: "24px" }}>
            <select
              value={partyId}
              onChange={e => setPartyId(e.target.value)}
              style={{ width: "100%", padding: "16px", fontSize: "18px", fontWeight: "700", borderRadius: "14px", border: `2px solid ${!partyId ? UI_THEME.warning : UI_THEME.border}`, background: "#fff", position: "relative", zIndex: 60, outline: "none", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}
            >
              <option value="">-- CHOOSE YOUR NAME --</option>
              {parties.map(p => <option key={p.partyId} value={p.partyId}>{p.name}</option>)}
            </select>
          </div>
        )}

        {!partyId && !submitted && (
          <div style={{ marginTop: "40px", textAlign: "center", animation: "fadeIn 0.5s" }}>
            <div style={{ fontSize: "70px", marginBottom: "20px", animation: "bounce-c 2s infinite" }}>☝️</div>
            <h2 style={{ fontSize: "22px", fontWeight: "900", color: UI_THEME.text }}>CHOOSE YOUR NAME</h2>
            <p style={{ color: UI_THEME.muted, fontSize: "15px", marginTop: "8px", fontWeight: "600" }}>to start your daily entry</p>
            <style>{`
              @keyframes bounce-c { 0%, 20%, 50%, 80%, 100% {transform: translateY(0);} 40% {transform: translateY(-20px);} 60% {transform: translateY(-10px);} }
              @keyframes fadeIn { from {opacity: 0;} to {opacity: 1;} }
            `}</style>
          </div>
        )}

        {openingBalance !== null && !submitted && (
          <div style={{ animation: "fadeIn 0.4s" }}>
            
            <SectionCard title="Ledger Summary" icon="📅">
              <DataRow label="Opening Balance" value={fmt(openingBalance)} />
              <DataRow label={partyMode === "Cash" ? "Today's Orders" : "Pending Invoices"} value={fmt(orderAmount)} color={UI_THEME.primary} />
              <DataRow label="CDM Sales" value={fmt(cdmAmount)} />
              <DataRow label="Niche Brand Sales" value={fmt(nicheBrandAmount)} />
              <div style={{ marginTop: "10px", padding: "12px", background: "#f0f9ff", borderRadius: "12px", border: "1px solid #bae6fd" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "800", color: "#0369a1" }}>
                  <span>Grand Total Due</span>
                  <span>₹ {(Number(openingBalance) + Number(orderAmount) + Number(cdmAmount) + Number(nicheBrandAmount)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Collections" icon="🏦">
              <DataRow label="Bharatpe (Online)" value={fmt(onlineCollected)} color="#0369a1" />
              <DataRow label="Cash Collected" value={fmt(cashTotal)} color={UI_THEME.success} />
              <div style={{ marginTop: "10px", padding: "12px", background: "#f0fdf4", borderRadius: "12px", border: "1px solid #bbf7d0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "800", color: "#166534" }}>
                  <span>Total Collection</span>
                  <span>₹ {(Number(onlineCollected) + Number(cashTotal)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Cash Denominations" icon="💵">
              {DENOM_ORDER.map(d => (
                <DenomRow 
                  key={d} 
                  value={d} 
                  count={denoms[d]} 
                  onChange={e => setDenoms({ ...denoms, [d]: Number(e.target.value) })} 
                />
              ))}
              <div style={{ marginTop: "15px", paddingTop: "15px", borderTop: `2px dashed ${UI_THEME.border}` }}>
                <DataRow label="Cash Total" value={fmt(cashTotal)} color={UI_THEME.success} size="24px" />
              </div>
            </SectionCard>

            <div style={{ 
              background: Math.abs(closingBalance) < 1 ? UI_THEME.success : (closingBalance > 0 ? UI_THEME.danger : UI_THEME.warning),
              padding: "24px", borderRadius: "20px", color: "#fff", textAlign: "center", boxShadow: "0 8px 24px rgba(0,0,0,0.15)", marginBottom: "30px"
            }}>
              <div style={{ fontSize: "14px", fontWeight: "900", textTransform: "uppercase", opacity: 0.9, letterSpacing: "0.1em", marginBottom: "8px" }}>
                {Math.abs(closingBalance) < 1 ? "✅ Balanced & Ready" : (closingBalance > 0 ? "⚠️ SHORTAGE" : "💰 EXTRA CASH FOUND")}
              </div>
              <div style={{ fontSize: "36px", fontWeight: "900", fontVariantNumeric: "tabular-nums" }}>
                ₹ {Math.abs(closingBalance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: "12px", opacity: 0.8, marginTop: "4px" }}>Closing Balance for Tomorrow</div>
            </div>

            <button
              onClick={submit}
              disabled={saving || countdown > 0}
              style={{
                width: "100%", padding: "20px", fontSize: "20px", fontWeight: "900",
                background: saving || countdown > 0 ? "#94a3b8" : UI_THEME.primary,
                color: "#fff", border: "none", borderRadius: "18px", boxShadow: "0 10px 15px -3px rgba(79, 70, 229, 0.3)", cursor: "pointer"
              }}
            >
              {saving ? "🔄 Saving Entry..." : (countdown > 0 ? `⌛ WAIT ${countdown}S (SYNCING)` : "FINAL SUBMIT & CLOSE →")}
            </button>
          </div>
        )}

        {submitted && preview && (
          <div style={{ animation: "fadeIn 0.5s" }}>
            <div style={{ 
              background: preview.status === "APPROVED" ? "#ecfdf5" : "#fffbeb", 
              padding: "20px", borderRadius: "18px", border: `2px solid ${preview.status === "APPROVED" ? UI_THEME.success : UI_THEME.warning}`,
              marginBottom: "24px", textAlign: "center", boxShadow: "0 4px 6px rgba(0,0,0,0.05)"
            }}>
              <div style={{ fontSize: "40px", marginBottom: "10px" }}>{preview.status === "APPROVED" ? "🎉" : "⏳"}</div>
              <h3 style={{ margin: 0, color: preview.status === "APPROVED" ? "#065f46" : "#92400e", fontSize: "22px", fontWeight: "900" }}>
                {preview.status === "APPROVED" ? "APPROVED BY CASHIER" : "SUBMITTED"}
              </h3>
              <p style={{ margin: "5px 0 0", color: "#6b7280", fontSize: "14px" }}>{preview.status === "APPROVED" ? "Day closed successfully." : "Waiting for cashier verification."}</p>
            </div>

            <SectionCard title="Submission Summary" icon="📋">
              <DataRow label="Customer" value={preview.partyId} size="20px" />
              <DataRow label="Opening Balance" value={fmt(preview.openingBalance)} />
              <DataRow label="Sales volume (Current)" value={fmt(preview.orderAmount)} />
              <DataRow label="CDM + Niche Sales" value={fmt(Number(preview.cdmAmount||0)+Number(preview.nicheBrandAmount||0))} />
              <div style={{ margin: "10px 0", borderBottom: `1px dashed ${UI_THEME.border}` }} />
              <DataRow label="Grand Total Due" value={fmt(Number(preview.openingBalance||0) + Number(preview.orderAmount||0) + Number(preview.cdmAmount||0) + Number(preview.nicheBrandAmount||0))} color={UI_THEME.text} />
              <DataRow label="Total Collected (Cash+Online)" value={fmt(Number(preview.cashTotal||0) + Number(preview.onlineCollected||0))} color={UI_THEME.success} />
              
              <div style={{ marginTop: "15px", padding: "12px", background: Math.abs(preview.closingBalance) < 1 ? "#f0fdf4" : (preview.closingBalance > 0 ? "#fef2f2" : "#fffbeb"), borderRadius: "12px", border: `1px solid ${Math.abs(preview.closingBalance) < 1 ? "#bbf7d0" : (preview.closingBalance > 0 ? "#fecaca" : "#fef3c7")}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "900", color: Math.abs(preview.closingBalance) < 1 ? "#166534" : (preview.closingBalance > 0 ? UI_THEME.danger : UI_THEME.warning) }}>
                  <span>{Math.abs(preview.closingBalance) < 1 ? "STATUS: BALANCED" : (preview.closingBalance > 0 ? "⚠️ SHORTAGE" : "💰 EXTRA CASH")}</span>
                  <span>₹ {fmt(Math.abs(preview.closingBalance))}</span>
                </div>
              </div>
            </SectionCard>
            
            <button
              onClick={() => navigate("/base")}
              style={{ width: "100%", padding: "18px", background: "#fff", border: `2px solid ${UI_THEME.primary}`, color: UI_THEME.primary, borderRadius: "16px", fontWeight: "900", fontSize: "18px" }}
            >
              Back to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
